import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ClipboardList, Cpu, ExternalLink, ImageIcon, MessageSquare, Pencil, Ruler, Save, TriangleAlert, Users, X } from "lucide-react";
import { api, BID_CAP } from "@/lib/api";
import { BidForm } from "./BidForm";
import { deriveInstallInfo, type Case } from "@shitamy/types";

interface Props { caseId: string; onBack: () => void; }

export function CaseDetailPage({ caseId, onBack }: Props) {
  const { data: c, isLoading, error } = useQuery({
    queryKey: ["case", caseId],
    queryFn: () => api.getCase(caseId),
  });

  // 応札枠（残り）— 早い者勝ち3社まで（sql/10）。満枠なら入札フォームを閉じる。
  const { data: bidCount } = useQuery({
    queryKey: ["case-bid-count", caseId],
    queryFn: () => api.casePendingCount(caseId),
    refetchInterval: 30_000,
  });

  if (isLoading) return <p className="p-4 text-sm">読み込み中...</p>;
  if (error || !c) return <p className="p-4 text-sm text-red-600">読み込み失敗</p>;

  const remaining = Math.max(BID_CAP - (bidCount ?? 0), 0);
  const full = remaining <= 0;

  return (
    <div className="px-4 py-4 space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-ink-soft">
        <ChevronLeft size={16} /> 案件一覧
      </button>

      <section>
        {c.customer_name && (
          <p className="text-xs font-bold text-brand-700 dark:text-brand-300">{c.customer_name}案件</p>
        )}
        <h2 className="text-lg font-semibold">{[c.prefecture, c.city].filter(Boolean).join(" ") || "エリア未設定"}</h2>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {c.customer_specs?.service && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.customer_specs.service === "clean" ? "bg-brand-100 text-brand-700 dark:bg-brand-800/40 dark:text-brand-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"}`}>
              {c.customer_specs.service === "clean" ? "エアコン洗浄" : "エアコン交換"}
            </span>
          )}
          <span className="text-xs text-ink-soft">
            {acTypeLabel(c.ac_type)}
            {c.preferred_date ? ` ／ 希望日 ${new Date(c.preferred_date).toLocaleDateString("ja-JP")}` : ""}
          </span>
        </div>
      </section>

      <PhotosSection caseId={c.id} />

      <InstallInfoSection c={c} />

      <CustomerSpecsSection c={c} />

      <AiResultSection c={c} />

      {c.customer_comment && (
        <section>
          <h3 className="flex items-center gap-2 text-sm font-semibold mb-2">
            <MessageSquare size={16} /> 顧客コメント
          </h3>
          <p className="text-sm bg-brand-50 dark:bg-brand-800/30 rounded-lg p-3">{c.customer_comment}</p>
        </section>
      )}

      <section>
        <p
          className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg ${
            full
              ? "bg-gray-100 text-ink-soft dark:bg-white/10 dark:text-white/50"
              : remaining === 1
              ? "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300"
              : "bg-brand-50 text-brand-700 dark:bg-brand-800/30 dark:text-brand-200"
          }`}
        >
          <Users size={13} />
          {full ? `応札枠（${BID_CAP}社）が埋まりました` : `応札枠 残り${remaining}社（先着${BID_CAP}社まで）`}
        </p>
      </section>

      {full ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-gray-50 dark:bg-white/5 p-4 text-sm text-ink-soft dark:text-white/60 mt-2">
          この案件はすでに{BID_CAP}社の応札で枠が埋まりました。別の案件をご検討ください。
        </div>
      ) : (
        <BidForm caseId={c.id} minAmount={minBidFor(c.ac_type)} />
      )}
    </div>
  );
}

/** 現場写真（非訪問見積もりの主材料）。署名URLで表示、タップで拡大。 */
function PhotosSection({ caseId }: { caseId: string }) {
  const [zoom, setZoom] = useState<string | null>(null);
  const { data: photos, isLoading } = useQuery({
    queryKey: ["case-photos", caseId],
    queryFn: () => api.listCasePhotoUrls(caseId),
    staleTime: 30 * 60_000, // 署名URLは60分有効
  });

  return (
    <section>
      <h3 className="flex items-center gap-2 text-sm font-semibold mb-2">
        <ImageIcon size={16} /> 現場写真{photos ? `（${photos.length}枚）` : ""}
      </h3>
      {isLoading && <p className="text-xs text-ink-soft">写真を読み込み中...</p>}
      {photos && photos.length === 0 && (
        <p className="text-xs text-ink-soft">写真が登録されていません</p>
      )}
      {photos && photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((ph) => (
            <button key={ph.path} onClick={() => setZoom(ph.url)} aria-label="写真を拡大">
              <img
                src={ph.url}
                alt="現場写真"
                loading="lazy"
                className="w-full aspect-square object-cover rounded-lg border border-[var(--color-border)]"
              />
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-ink-soft mt-1">
        ※ タップで拡大。銘板・配管・電源・設置場所をご確認ください。
      </p>

      {zoom && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-2"
          onClick={() => setZoom(null)}
        >
          <button
            aria-label="閉じる"
            className="absolute top-3 right-3 p-2 rounded-full bg-white/15 text-white"
            onClick={() => setZoom(null)}
          >
            <X size={22} />
          </button>
          <img src={zoom} alt="現場写真 拡大" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </section>
  );
}

/**
 * AI抽出結果＋業者フィードバック。
 * AIは情報抽出まで（価格は出さない）。業者が品番/能力/冷媒を修正でき、保存は「業者フィードバック」＝学習データの種。
 * 品番は外部検索リンク（Google/Yahoo）で逃がす（カタログ/画像確認用）。
 */
function AiResultSection({ c }: { c: Case }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [model, setModel] = useState(c.ai_model_guess ?? "");
  const [capacity, setCapacity] = useState(c.ai_capacity ?? "");
  const [refrigerant, setRefrigerant] = useState(c.customer_specs?.refrigerant ?? "");

  const saveMut = useMutation({
    mutationFn: () =>
      api.updateCaseAiFields(
        c.id,
        { model, capacity, refrigerant },
        c.customer_specs,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["case", c.id] });
      setEditing(false);
    },
  });

  const startEdit = () => {
    setModel(c.ai_model_guess ?? "");
    setCapacity(c.ai_capacity ?? "");
    setRefrigerant(c.customer_specs?.refrigerant ?? "");
    setEditing(true);
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Cpu size={16} /> AI抽出結果
        </h3>
        {!editing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-300 font-medium"
          >
            <Pencil size={13} /> 修正する
          </button>
        )}
      </div>

      {editing ? (
        <div className="rounded-xl border border-[var(--color-border)] p-3 space-y-3 text-sm">
          <EditRow label="品番" value={model} onChange={setModel} placeholder="例: RAS-2811V" />
          <EditRow label="能力" value={capacity} onChange={setCapacity} placeholder="例: 2.8kW / 10畳用" />
          <EditRow label="冷媒" value={refrigerant} onChange={setRefrigerant} placeholder="例: R32" />
          <p className="text-[11px] text-ink-soft">
            ※ 写真で確認した実機の値に修正してください。修正内容は業者フィードバックとして記録されます。
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              className="flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-bold px-3 py-2 rounded-xl active:scale-95 transition"
            >
              <Save size={15} /> {saveMut.isPending ? "保存中..." : "修正を保存"}
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={saveMut.isPending}
              className="text-sm text-ink-soft px-3 py-2"
            >
              キャンセル
            </button>
          </div>
          {saveMut.error && <p className="text-xs text-red-600">{(saveMut.error as Error).message}</p>}
        </div>
      ) : hasAi(c) || c.customer_specs?.refrigerant ? (
        <div className="rounded-xl border border-[var(--color-border)] p-3 space-y-1 text-sm">
          <KVModel k="機種推定（品番）" v={c.ai_model_guess} />
          <KV k="能力" v={fmtVal(c.ai_capacity)} />
          <KV k="冷媒" v={fmtVal(c.customer_specs?.refrigerant)} />
          <KV k="天井高" v={fmtVal(c.ai_ceiling_height)} />
          <KV k="配管" v={fmtVal(c.ai_piping)} />
          <KV k="電源" v={fmtVal(c.ai_power)} />
        </div>
      ) : (
        <p className="text-xs text-ink-soft">AI解析待ちです</p>
      )}

      <p className="text-[11px] text-ink-soft mt-1">
        ※ AIは情報抽出まで。見積りは業者の判断で作成してください。
        <br />
        ※ 品番・能力・冷媒は必ず写真でご確認ください（トラブル防止）。誤りは「修正する」で直してください。
      </p>
    </section>
  );
}

function EditRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs text-ink-soft">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
      />
    </label>
  );
}

/** 品番行：値があれば Google/Yahoo の外部検索リンクで逃がす（カタログ/画像確認用）。 */
function KVModel({ k, v }: { k: string; v?: string | null }) {
  const model = (v ?? "").trim();
  if (model === "") return <KV k={k} v="—" />;
  return (
    <div className="flex justify-between gap-3 items-start">
      <span className="text-ink-soft shrink-0">{k}</span>
      <span className="text-right break-all">
        <span className="font-medium">{model}</span>
        <span className="flex items-center justify-end gap-2 mt-0.5">
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(model)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-[11px] text-brand-600 dark:text-brand-300"
          >
            <ExternalLink size={11} /> Google
          </a>
          <a
            href={`https://search.yahoo.co.jp/search?p=${encodeURIComponent(model)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-[11px] text-brand-600 dark:text-brand-300"
          >
            <ExternalLink size={11} /> Yahoo
          </a>
        </span>
      </span>
    </div>
  );
}

/**
 * AI推定設置高さ（参考）＋施工情報（推奨脚立・必要人数・施工難易度）。中島社長アイデア（2026-07）。
 * 高さから導出。現場価値の底上げが狙い。価格は出さない（禁則順守）。
 */
function InstallInfoSection({ c }: { c: Case }) {
  const info = deriveInstallInfo(c.ai_install_height_m);
  if (!info) return null;
  return (
    <section>
      <h3 className="flex items-center gap-2 text-sm font-semibold mb-2">
        <Ruler size={16} /> 推定設置高さ・施工情報（参考）
      </h3>
      <div className="rounded-xl border border-brand-200 dark:border-brand-700 bg-brand-50/60 dark:bg-brand-800/20 p-3 space-y-2">
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-ink-soft">AI推定設置高さ</span>
          <span className="text-2xl font-black text-brand-600 dark:text-brand-300 leading-none">{info.heightLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <InfoMetric label="脚立の目安" value={info.ladderLabel} sub={info.ladderNote} />
          <InfoMetric label="必要人数" value={info.crewLabel} />
          <InfoMetric label="施工難易度" value={info.difficultyStars} />
        </div>
        <p className="text-[11px] text-ink-soft dark:text-white/50 flex items-start gap-1">
          <TriangleAlert size={12} className="mt-0.5 flex-shrink-0" />
          写真からのAI概算です。脚立・人員・安全対策は現場でご判断ください。
        </p>
      </div>
    </section>
  );
}

function InfoMetric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-white dark:bg-white/5 border border-[var(--color-border)] px-2 py-1.5 text-center">
      <div className="text-[10px] text-ink-soft dark:text-white/50">{label}</div>
      <div className="text-sm font-bold text-brand-700 dark:text-brand-200 leading-tight">{value}</div>
      {sub && <div className="text-[9px] text-ink-soft dark:text-white/40">{sub}</div>}
    </div>
  );
}

/** 顧客が手入力した参考情報（2026-06-10 中島社長決定：写真＋手入力で非訪問見積もりを成立させる）。 */
function CustomerSpecsSection({ c }: { c: Case }) {
  const s = c.customer_specs;
  if (!s) return null;
  const rows: Array<[string, string | null | undefined]> = [
    ["品番", s.model_number],
    ["能力", s.capacity],
    ["形", s.install_form],
    ["冷媒", s.refrigerant],
    ["補足", s.note],
  ];
  const present = rows.filter(([, v]) => v);
  if (present.length === 0) return null;
  return (
    <section>
      <h3 className="flex items-center gap-2 text-sm font-semibold mb-2">
        <ClipboardList size={16} /> 顧客入力の参考情報
      </h3>
      <div className="rounded-xl border border-[var(--color-border)] p-3 space-y-1 text-sm">
        {present.map(([k, v]) =>
          k === "品番" ? (
            <KVModel key={k} k={k} v={String(v)} />
          ) : (
            <KV key={k} k={k} v={String(v)} />
          ),
        )}
      </div>
      <p className="text-[11px] text-ink-soft mt-1">
        ※ 顧客の自己申告です。写真と併せてご確認ください。
      </p>
    </section>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-soft">{k}</span>
      <span className="font-medium text-right break-all">{v}</span>
    </div>
  );
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function hasAi(c: { ai_model_guess?: string | null; ai_capacity?: string | null; ai_ceiling_height?: string | null; ai_piping?: string | null; ai_power?: string | null }): boolean {
  return Boolean(c.ai_model_guess || c.ai_capacity || c.ai_ceiling_height || c.ai_piping || c.ai_power);
}

function acTypeLabel(t?: string | null) {
  switch (t) {
    case "room": return "ルームエアコン";
    case "package": return "パッケージエアコン";
    case "multi": return "マルチエアコン";
    case "commercial": return "業務用エアコン";
    default: return "エアコン工事";
  }
}

/** ダンピング防止の最低入札額（ac_type別）。ルーム¥35,000 / パッケージ以上¥150,000。 */
function minBidFor(t?: string | null): number {
  return t === "room" ? 35000 : 150000;
}

// build: install-height feature (trigger deploy) 2026-07-12
